import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Heart, ArrowRight } from 'lucide-react';
import { contactInfo } from '../mock';

const Footer = () => {
  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Story', path: '/about' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Wedding Gallery', path: '/portfolio' },
    { name: 'Become a Vendor', path: '/vendors/register' },
    { name: 'Contact Us', path: '/contact' }
  ];

  const serviceLinks = [
    { name: 'Destination Weddings', path: '/destination-weddings' },
    { name: 'Wedding Décor & Styling', path: '/wedding-decor' },
    { name: 'Pre & Post Wedding Events', path: '/complete-celebration' },
    { name: 'Corporate & Social Events', path: '/events-weddings' },
    { name: 'Wedding Proposal', path: '/wedding-proposal' },
    { name: 'Honeymoon Packages', path: '/honeymoon' }
  ];

  const destinationLinks = [
    { name: 'Wedding in Dharamshala', path: '/weddings/dharamshala' },
    { name: 'Wedding in McLeodganj', path: '/weddings/mcleodganj' },
    { name: 'Wedding in Palampur', path: '/weddings/palampur' },
    { name: 'Wedding in Kangra', path: '/weddings/kangra' }
  ];

  const moreDestinations = [
    { name: 'Destination Wedding in Manali', path: '/destinations/manali' },
    { name: 'Destination Wedding in Shimla', path: '/destinations/shimla' },
    { name: 'Destination Wedding in Dalhousie', path: '/destinations/dalhousie' },
    { name: 'Destination Wedding in Kasol', path: '/destinations/kasol' }
  ];

  const supportLinks = [
    { name: 'FAQs', path: '/faqs' },
    { name: 'Wedding Packages', path: '/packages' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms of Service', path: '/terms-of-service' },
    { name: 'Cancellation Policy', path: '/cancellation-policy' }
  ];

  return (
    <footer className="relative" data-testid="footer">
      {/* Elegant Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDF8F5] via-[#FBF3EE] to-[#F8EDE6]" />
      
      {/* Decorative Top Border */}
      <div className="relative h-1 bg-gradient-to-r from-transparent via-[#E91E8C] to-transparent" />
      
      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_dreamy-weddings/artifacts/h0d1bmq6_ChatGPT%20Image%20Jan%2014%2C%202026%2C%2004_05_46%20PM.png"
                alt="Destination Weddings and Events Logo"
                className="h-16 w-auto object-contain"
                style={{ filter: 'brightness(1) contrast(1.1)' }}
              />
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Creating unforgettable luxury wedding and event experiences in the breathtaking mountains of Himachal Pradesh.
            </p>
            
            {/* Social Icons - More Prominent */}
            <div className="space-y-4">
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:scale-105"
                style={{ 
                  background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
                }}
                data-testid="footer-instagram"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="h-5 w-5 text-white" aria-hidden="true" />
                <span className="text-white text-sm font-medium">Follow on Instagram</span>
              </a>
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 bg-[#1877F2] rounded-xl transition-all hover:scale-105"
                data-testid="footer-facebook"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="h-5 w-5 text-white" aria-hidden="true" />
                <span className="text-white text-sm font-medium">Follow on Facebook</span>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-[#2C2C2C] font-semibold mb-6 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-[#E91E8C] transition-colors text-sm flex items-center group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E91E8C]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-[#2C2C2C] font-semibold mb-6 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-[#E91E8C] transition-colors text-sm flex items-center group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E91E8C]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations Links - SEO Focused */}
          <div>
            <h4 className="text-[#2C2C2C] font-semibold mb-6 text-sm uppercase tracking-wider">Wedding Locations</h4>
            <ul className="space-y-3">
              {destinationLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-[#E91E8C] transition-colors text-sm flex items-center group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E91E8C]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h5 className="text-[#2C2C2C] font-semibold mt-6 mb-4 text-xs uppercase tracking-wider opacity-70">More Destinations</h5>
            <ul className="space-y-2">
              {moreDestinations.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-500 hover:text-[#E91E8C] transition-colors text-xs flex items-center group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="h-2 w-2 mr-1 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E91E8C]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-[#2C2C2C] font-semibold mb-6 text-sm uppercase tracking-wider">Support</h4>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-[#E91E8C] transition-colors text-sm flex items-center group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E91E8C]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Bar */}
        <div className="mt-12 pt-8 border-t border-[#E91E8C]/20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#FF1493] flex items-center justify-center shadow-lg">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider">Call Us</p>
                <a href={`tel:${contactInfo.phones[0]}`} className="text-[#2C2C2C] hover:text-[#E91E8C] transition-colors text-sm font-semibold">
                  {contactInfo.phones[0]}
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#FF1493] flex items-center justify-center shadow-lg">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider">Email Us</p>
                <a href={`mailto:${contactInfo.email}`} className="text-[#2C2C2C] hover:text-[#E91E8C] transition-colors text-sm font-semibold">
                  {contactInfo.email}
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#FF1493] flex items-center justify-center shadow-lg">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider">Location</p>
                <span className="text-[#2C2C2C] text-sm font-semibold">{contactInfo.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#E91E8C]/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm flex items-center">
              © 2026 Destination Weddings & Events. All Rights Reserved. Made with 
              <Heart className="h-3 w-3 mx-1 text-[#E91E8C]" fill="#E91E8C" />
              in Himachal Pradesh
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-[#E91E8C] transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-gray-500 hover:text-[#E91E8C] transition-colors">Terms of Service</Link>
            </div>
          </div>
          
          {/* Local SEO Line */}
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-sm">
              <strong>Best Event Organiser in Dharamshala</strong> • Wedding Planner • <strong>Wedding Decorator in Dharamshala</strong> • <strong>Corporate Event Planner in Dharamshala</strong> • Find Best Venue for Wedding in Dharamshala
            </p>
          </div>
          
          {/* Unit of Pretty Planet */}
          <div className="mt-4 pt-4 border-t border-[#E91E8C]/10 text-center">
            <p className="text-gray-500 text-sm">
              Destination Weddings & Events is a unit of{' '}
              <a 
                href="https://prettyplanettravels.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#E91E8C] hover:text-[#D91880] transition-colors font-medium"
              >
                Pretty Planet Travels & Events
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
